import * as THREE from 'three';
import { titleSlice, viewRectForLabel } from '../tagLayout';
import { titleEllipse } from './geometry';
import { hit, ndc, plane, ray, tagWorld } from './vectors';

export function tagPixelBox(ndcPt, lwPx, lhPx, width, height) {
  const sx = (ndcPt.x * 0.5 + 0.5) * width;
  const sy = (-ndcPt.y * 0.5 + 0.5) * height;
  return { left: sx - lwPx * 0.5, top: sy - lhPx, w: lwPx, h: lhPx };
}

export function projectEllipseAabb(camera, bunch, hw, hh) {
  const z = bunch.userData.tagZ ?? 0.45;
  const cx = bunch.position.x + (bunch.userData.contentOffX ?? 0);
  const cy = bunch.position.y + (bunch.userData.contentOffY ?? 0);
  const pts = [
    [0, 0], [-hw, 0], [hw, 0], [0, -hh], [0, hh],
    [-hw, -hh], [hw, -hh], [-hw, hh], [hw, hh],
  ];
  let left = Infinity;
  let right = -Infinity;
  let bottom = Infinity;
  let top = -Infinity;
  let any = false;
  for (let i = 0; i < pts.length; i += 1) {
    tagWorld.set(cx + pts[i][0], cy + pts[i][1], z);
    ndc.copy(tagWorld).project(camera);
    if (ndc.z < -1 || ndc.z > 1) continue;
    any = true;
    if (ndc.x < left) left = ndc.x;
    if (ndc.x > right) right = ndc.x;
    if (ndc.y < bottom) bottom = ndc.y;
    if (ndc.y > top) top = ndc.y;
  }
  return any ? { left, right, bottom, top } : null;
}

export function bunchCenterNdc(camera, bunch) {
  const z = bunch.userData.tagZ ?? 0.45;
  tagWorld.set(
    bunch.position.x + (bunch.userData.contentOffX ?? 0),
    bunch.position.y + (bunch.userData.contentOffY ?? 0),
    z,
  );
  ndc.copy(tagWorld).project(camera);
  return ndc.z > -1 && ndc.z < 1 ? { x: ndc.x, y: ndc.y } : null;
}

export function visibleCloudRect(camera, bunch, lw, lh, pad, topInset = 0, leftInset = 0) {
  const title = titleEllipse(bunch);
  const aabb = projectEllipseAabb(camera, bunch, title.hw, title.hh);
  const view = viewRectForLabel(lw, lh, pad, topInset, leftInset);
  return titleSlice(aabb, view, bunchCenterNdc(camera, bunch));
}

export function ndcToLocalOnBunch(camera, bunch, ndcX, ndcY) {
  const z = bunch.userData.tagZ ?? 0.45;
  ndc.set(ndcX, ndcY, 0.5).unproject(camera);
  ray.origin.copy(camera.position);
  ray.direction.copy(ndc).sub(camera.position).normalize();
  plane.set(new THREE.Vector3(0, 0, 1), -z);
  if (!ray.intersectPlane(plane, hit)) return null;
  return { x: hit.x - bunch.position.x, y: hit.y - bunch.position.y, z };
}

export function projectLocal(camera, bunch, local) {
  tagWorld.set(bunch.position.x + local.x, bunch.position.y + local.y, local.z);
  ndc.copy(tagWorld).project(camera);
  return ndc.z > -1 && ndc.z < 1 ? { x: ndc.x, y: ndc.y } : null;
}

/** Project current cluster titles into host pixels. */
export function projectNebulaTitleBoxes(group, camera, container) {
  const boxes = [];
  if (!group || !camera || !container) return boxes;
  const width = Math.max(container.clientWidth, 1);
  const height = Math.max(container.clientHeight, 1);
  group.children.forEach((bunch) => {
    const tag = bunch.userData?.tag;
    const el = tag?.element;
    if (!tag || !el || el.dataset.editing === '1') return;
    if (el.style.visibility === 'hidden' || parseFloat(el.style.opacity) < 0.08) return;
    tag.getWorldPosition(tagWorld);
    ndc.copy(tagWorld).project(camera);
    if (ndc.z < -1 || ndc.z > 1) return;
    const w = el.offsetWidth || 80;
    const h = el.offsetHeight || 16;
    boxes.push(tagPixelBox(ndc, w, h, width, height));
  });
  return boxes;
}
